import { createGlobalState, useAsyncState } from '@vueuse/core'
import { computed, toRaw, watch } from 'vue'
import { http } from '@/utils/http'
import type { Response } from '@/types/http'
import { type DiscountDTO } from '@/types/discounts'
import { addHttpPrefix } from '@/utils'
import { getCurrentLanguage } from '@/i18n'
import type { WithRequired } from '@/types/utility'
import { useToken } from './useToken'
import { TerminalType } from '@/types/common'
import { getLocaleField } from '@/utils/localeMapping'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import type { LangCode } from '@/types/config'
// 视图层使用的数据模型
export interface ActivityItem {
  imageUrl?: string
  activityId: string
  key: string
  label: string
  activityType?: number
  activityName?: string
  activityNameZh?: string
  activityNameEn?: string
  activityNameVi?: string
  activityNameHi?: string
  activityNameTa?: string
  activityNameMs?: string
  activityNamePt?: string
  showType?: number
  claimed?: number
  // 周期结束时间字符串
  endDate: string
}

export type ActivityList = ActivityItem[]
type Clean = WithRequired<DiscountDTO, 'imageUrlPc' | 'activityType' | 'activityId'>

const normalizeActivityType = (type?: number | string) => {
  if (type === undefined || type === null) return NaN
  return Number(type)
}

async function fetchActivityListApi(params?: Record<string, any>) {
  return http<Response<DiscountDTO[]>>('/app-api/activity/index/list', {
    method: 'POST',
    body: params || {}
  })
}

/**
 * PC 活动列表**统一**数据源。
 *
 * 内部封装活动列表接口请求、PC 端活动过滤、视图层数据格式化、全局共享状态，
 *
 * 登录成功后主动刷新列表，用于更新 claimed/红点等用户相关数据。
 */
export const useActivityListData = createGlobalState(() => {
  const { isLoggedIn } = useToken()
  const { locale } = useI18n()
  // 获取原始数据并做通用清洗
  const fetchRawActivityList = async () => {
    const rawList: DiscountDTO[] = (await fetchActivityListApi()).data ?? []
    // console.log('rawList', rawList)
    const cleanList = rawList.filter((item): item is Clean => {
      return (
        item.terminalOptions.includes(TerminalType._web) &&
        !!item.activityId &&
        item.activityType != undefined
      )
    })
    return cleanList
  }

  const {
    state: rawActivities,
    isLoading,
    error,
    execute: refreshActivityList
  } = useAsyncState<Clean[]>(fetchRawActivityList, [], {
    immediate: true,
    resetOnExecute: false
  })

  watch(isLoggedIn, (v) => {
    if (v) {
      // 登录后主动更新活动最新数据（更新列表红点信息）
      refreshActivityList()
    }
  })

  // 数据格式化，为视图层服务
  const activityList = computed(() => {
    return (rawActivities.value || []).map((item): ActivityItem => {
      const label =
        locale.value === 'zh-CN'
          ? item.activityName
          : getLocaleField(item, locale.value as LangCode, 'activityName')
      const imageUrl = getLocaleField(item, locale.value as LangCode, 'image', 'UrlPc')

      // console.log('label', label, locale.value, item)

      return {
        imageUrl: addHttpPrefix(imageUrl),
        activityId: String(item.activityId),
        activityType: item.activityType,
        activityNameZh: item.activityNameZh || item.activityName || '',
        activityNameEn: item.activityNameEn || '',
        activityNameVi: item.activityNameVi || '',
        activityNameHi: item.activityNameHi || '',
        activityNameTa: item.activityNameTa || '',
        activityNameMs: item.activityNameMs || '',
        activityNamePt: item.activityNamePt || '',
        activityName: label || item.activityName || '',
        key: String(item.activityId),
        label: label || item.activityName || '',
        showType: item.showType,
        claimed: item.claimed,
        endDate: dayjs(item.periodEndTime).format('MM/DD/YYYY')
      }
    })
  })

  const activityListWithBanner = computed(() => {
    return activityList.value.filter((item) => item.imageUrl)
  })

  const getByType = (type?: number | string) => {
    const targetType = normalizeActivityType(type)
    if (Number.isNaN(targetType)) return null
    return activityList.value.find((item) => Number(item.activityType) === targetType) ?? null
  }

  return {
    // 视图层数据
    activityList,
    // 有banner的活动  为了UI好看
    activityListWithBanner,
    // 原始状态
    rawActivities,
    isLoading,
    error,
    // 查找某类型活动
    getByType,
    // 刷新
    refreshActivityList
  }
})
